/**
 * Client for the Email Routes admin API (superadmin) — the master control for every
 * outbound email route. See api/routes/admin/emailRoutes.js.
 */
import { adminFetch } from './adminApi';

export interface EmailRoute {
  key: string;
  label: string;
  description: string;
  /** Customer/outside-facing → the UI confirms before enabling. */
  external: boolean;
  /** The DB toggle. */
  enabled: boolean;
  /** Per-route env override: true = forced on, false = kill switch, null = DB governs. */
  envOverride: boolean | null;
  /** What the route sends through: email (Resend) or Apple push (Pocket). Older servers omit it. */
  channel?: 'email' | 'push';
  /** Whether its service is set up: the Resend key, or for push an APNs key (it sends only when enabled AND this). */
  keyConfigured: boolean;
  /** Push only: APNs environments whose key Apple refused on its latest answer, and when. */
  pushKeyRefused?: Array<{ environment: 'sandbox' | 'production'; at: string | null }>;
  defaultEnabled: boolean;
  /** A launch-gated route that cannot be enabled yet (server refuses). */
  launchLocked?: boolean;
  /** Why it's locked, for the panel. */
  lockedReason?: string;
  /** Only a superadmin membership may change it (the customer-reports go-live switch). */
  superadminMemberOnly?: boolean;
  /** Whether the signed-in person may flip it (absent from an older API: treat as yes). */
  canChange?: boolean;
}

/** Every route with its resolved state. */
export async function getEmailRoutes(): Promise<{ routes: EmailRoute[] }> {
  return adminFetch('/email-routes');
}

/** Flip one route's toggle. Enabling an external route starts real outbound email. */
export async function setEmailRoute(route: string, enabled: boolean): Promise<{ key: string; enabled: boolean; envOverride: boolean | null }> {
  return adminFetch(`/email-routes/${encodeURIComponent(route)}`, { method: 'PUT', body: JSON.stringify({ enabled }) });
}
